import React, { useState } from 'react';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import {
  C4DiagramModel,
  C4DiagramDeploymentNode,
  C4DiagramRelation,
  toC4DiagramDeploymentNode,
  toC4DiagramEntity,
} from '../C4DiagramCard/types';
// @ts-ignore
import * as uuid from 'uuid';
import { useOwnedResources } from '../../../hooks';
import { Select, SelectedItems } from '@backstage/core-components';
import { CircularProgress } from '@material-ui/core';
import { C4DeploymentDiagramCard } from '../C4DeploymentDiagramCard';

export const DeploymentDiagramCard = () => {
  const environments = [
    { label: 'Production', value: 'prod' },
    { label: 'Pre-Production', value: 'preprod' },
    { label: 'Laboratorium', value: 'lab' },
    { label: 'Develop', value: 'dev' },
    { label: 'Sandbox', value: 'sbx' },
  ];
  const [environment, setEnvironment] = useState<SelectedItems>('prod');
  const { entity } = useEntity();
  const ownedResources = useOwnedResources();
  const relatedComponents = useRelatedEntities(entity, { kind: 'Component' });

  if (
    !ownedResources.loading &&
    !relatedComponents.loading &&
    entity?.metadata
  ) {
    const idMapping = new Map<string, string[]>();
    const model: C4DiagramModel = {
      type: 'deployment',
      context: toC4DiagramEntity(entity),
      entities: [],
      relations: [],
    };
    model.context!.entities = [];

    // Component Container Model
    const systemComponents = relatedComponents.entities?.filter(
      it => it.spec?.system === entity.metadata.name,
    );
    const components = systemComponents?.map(it => toC4DiagramEntity(it));
    const resources = createDeploymentNodes(
      environment as string,
      ownedResources.entities,
    );
    const relations: C4DiagramRelation[] = [];
    if (systemComponents) {
      // First process all components/container because they get a new uuid, as conatiner can be deployed in multiple resources
      for (const systemComponent of systemComponents) {
        pushDeploymentNodes(
          model,
          environment as string,
          idMapping,
          ownedResources.entities,
          systemComponent,
        );
        for (const relation of systemComponent.relations
          ? systemComponent.relations
          : []) {
          const target = parseEntityRef(relation.targetRef);
          if (target.kind === 'component') {
            const targetComponent = systemComponents.find(
              it => it.metadata.name === target.name,
            );
            const rel = (
              systemComponent.spec?.dependsOnComponents as any[]
            )?.find(rel => rel.name === target.name);
            if (targetComponent && relation.type === 'dependsOn') {
              relations.push({
                id: uuid.v4(),
                source: systemComponent.metadata.name,
                target: targetComponent.metadata.name,
                text: rel?.metadata?.description,
              });
            }
          }
        }
      }
      // Now process all relations an duplicate the relations for multiple deployed containers
      for (const systemComponent of systemComponents) {
        for (const relation of systemComponent.relations
          ? systemComponent.relations
          : []) {
          const target = parseEntityRef(relation.targetRef);
          if (target.kind === 'component') {
            const targetComponent = systemComponents.find(
              it => it.metadata.name === target.name,
            );
            const rel = (
              systemComponent.spec?.dependsOnComponents as any[]
            )?.find(rel => rel.name === target.name);
            if (targetComponent && relation.type === 'dependsOn') {
              idMapping.get(systemComponent.metadata.name)?.forEach(source => {
                idMapping
                  .get(targetComponent.metadata.name)
                  ?.forEach(target => {
                    pushRelation(model, {
                      id: uuid.v4(),
                      source: source,
                      target: target,
                      text: rel?.metadata?.description,
                    });
                  });
              });
            }
          }
        }
      }
    }
    return (
      <>
        <Select
          label="Environment"
          items={environments}
          onChange={(e: SelectedItems) => {
            setEnvironment(e);
          }}
          multiple={false}
          selected={environment}
          margin="dense"
        ></Select>
        <C4DeploymentDiagramCard
          model={model}
          components={components}
          rescources={resources}
          relations={relations}
        />
      </>
    );
  }
  return (
    <>
      <p>Loading the diagram!</p>
      <CircularProgress />
    </>
  );
};

function createDeploymentNodes(
  environment: string,
  entities?: Entity[],
): C4DiagramDeploymentNode[] {
  const filteredEntities = entities?.filter(
    it => it.metadata.tags?.includes(environment) || it.kind === 'Product',
  );
  const resources = filteredEntities?.map(it => toC4DiagramDeploymentNode(it));
  filteredEntities?.forEach(it => {
    if (it.relations) {
      const resource = resources?.find(res => res.id === it.metadata.name)!;
      for (const rel of it.relations) {
        if (rel.type === 'deployedIn') {
          const target = parseEntityRef(rel.targetRef);
          const parent = resources?.find(res => res.id === target.name);
          if (parent) {
            if (!parent.deploymentNodes) {
              parent.deploymentNodes = [];
            }
            parent.deploymentNodes.push(resource);
            resource.parent = parent.id;
          }
        }
      }
    }
  });
  return resources || [];
}

function findDeploymentNode(
  deploymentNodeId: string,
  deploymentNodes?: C4DiagramDeploymentNode[],
): C4DiagramDeploymentNode | undefined {
  if (deploymentNodes) {
    for (const node of deploymentNodes) {
      if (node.id === deploymentNodeId) {
        return node;
      } else {
        const deploymentNode = findDeploymentNode(
          deploymentNodeId,
          node.deploymentNodes,
        );
        if (deploymentNode) {
          return deploymentNode;
        }
      }
    }
  }
  return undefined;
}

function pushDeploymentNodes(
  model: C4DiagramModel,
  environment: string,
  idMapping: Map<string, string[]>,
  ownedResources?: Entity[],
  entity?: Entity,
) {
  const deployedInIds = entity?.relations
    ?.filter(it => it.type === 'deployedIn')
    .map(it => parseEntityRef(it.targetRef))
    .map(it => it.name);
  if (deployedInIds && deployedInIds.length > 0) {
    for (const deployedInId of deployedInIds) {
      const parentResource = ownedResources
        ?.filter(
          it =>
            it.metadata.tags?.includes(environment) || it.kind === 'Product',
        )
        .find(it => it.metadata.name === deployedInId);
      if (parentResource) {
        pushDeploymentNodes(
          model,
          environment,
          idMapping,
          ownedResources,
          parentResource,
        );
        const parentDeploymentNode = findDeploymentNode(
          parentResource.metadata.name,
          model.deploymentNodes,
        )!;
        if (parentDeploymentNode) {
          if (entity?.kind === 'Resource') {
            const deploymentNode = findDeploymentNode(
              entity.metadata.name,
              model.deploymentNodes,
            );
            if (!deploymentNode) {
              if (!parentDeploymentNode.deploymentNodes) {
                parentDeploymentNode.deploymentNodes = [];
              }
              parentDeploymentNode.deploymentNodes.push(
                toC4DiagramDeploymentNode(entity),
              );
            }
          } else if (entity?.kind === 'Component') {
            pushDeploymentNodeEntity(entity, parentDeploymentNode, idMapping);
          }
        }
      }
    }
  } else if (entity?.kind === 'Resource' || entity?.kind === 'Product') {
    // Root node
    const deploymentNode = findDeploymentNode(
      entity.metadata.name,
      model.deploymentNodes,
    );
    if (!deploymentNode) {
      if (!model.deploymentNodes) {
        model.deploymentNodes = [];
      }
      model.deploymentNodes.push(toC4DiagramDeploymentNode(entity));
    }
  }
}

function pushDeploymentNodeEntity(
  entity: Entity,
  deploymentNode: C4DiagramDeploymentNode,
  idMapping: Map<string, string[]>,
) {
  let innerDeploymentNode: C4DiagramDeploymentNode | undefined = deploymentNode;
  const technology = entity.metadata?.annotations
    ? entity.metadata?.annotations['rtl.de/technology']
    : undefined;
  if (technology) {
    if (!deploymentNode.deploymentNodes) {
      deploymentNode.deploymentNodes = [];
    }
    innerDeploymentNode = deploymentNode.deploymentNodes.find(
      it => it.text === technology,
    );
    if (!innerDeploymentNode) {
      innerDeploymentNode = {
        id: uuid.v4(),
        text: technology,
        technology: true,
        parent: deploymentNode.id,
      };
      deploymentNode.deploymentNodes?.push(innerDeploymentNode);
    }
  }
  if (!innerDeploymentNode.entities) {
    innerDeploymentNode.entities = [];
  }

  const c4DiagramEntry = {
    id: uuid.v4(),
    entity: toC4DiagramEntity(entity),
  };
  if (!idMapping.get(entity.metadata.name)) {
    idMapping.set(entity.metadata.name, []);
  }
  idMapping.get(entity.metadata.name)?.push(c4DiagramEntry.id);
  innerDeploymentNode.entities?.push(c4DiagramEntry);
}

function pushRelation(model: C4DiagramModel, relation: C4DiagramRelation) {
  if (
    !model.relations.find(
      it => it.source === relation.source && it.target == relation.target,
    )
  ) {
    model.relations.push(relation);
  }
}
